import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Food & Beverage",
  "Nutraceuticals",
  "Personal Care",
  "Pet Food",
  "Industrial",
  "Other",
];

const PRODUCTS = [
  "Stand-up Pouch",
  "Spouted Pouch",
  "Center-seal Bag",
  "3-side Seal Sachet",
  "Flat Bottom Pouch",
  "Custom Die-cut",
];

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  industry: "",
  product_type: "",
  moq: "",
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.industry ||
      !form.product_type
    ) {
      toast.error("Please fill name, email, industry and product type.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Quote request received. Our team will reach out within 24 hrs.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative bg-white py-20 md:py-28"
    >
      <div className="absolute inset-0 pk-grid-bg opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {/* Left — pitch + contact */}
          <div className="col-span-12 lg:col-span-5 flex flex-col">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#1B3F8F] mb-4 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#F58220]" />
              Start a project
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-tighter">
              Tell us about<br />
              the <span className="text-[#F58220]">pouch</span> you<br />
              want to ship.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#0a1128]/75 font-medium leading-relaxed max-w-md">
              Reply within 24 hours. Free design audit on every quote. Samples
              despatched in 48 hours after artwork lock.
            </p>

            <div className="mt-10 space-y-5 max-w-md">
              <ContactRow
                Icon={Phone}
                label="Talk to sales"
                value="+91 93279 03300"
                href="tel:+919327903300"
              />
              <ContactRow
                Icon={Mail}
                label="Email"
                value="hello@pouchkart.co.in"
                href="mailto:hello@pouchkart.co.in"
              />
              <ContactRow
                Icon={MapPin}
                label="Visit the factory"
                value="Pacific Laminates, Pardi, Gujarat"
              />
            </div>

            <div className="mt-10 border-t border-[#d1d5db] pt-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#1B3F8F]">
                Trusted by
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {["FMCG-1", "STARTUP-2", "D2C-3"].map((b) => (
                  <div
                    key={b}
                    className="h-12 flex items-center justify-center border border-[#d1d5db] text-[10px] font-bold uppercase tracking-[0.18em] text-[#0a1128]/60"
                  >
                    {b}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-7 bg-[#0a1128] text-white p-8 md:p-12 border border-[#0a1128] relative"
          >
            <div className="absolute -top-3 -left-3 right-3 bottom-3 border border-[#F58220] -z-0 pointer-events-none hidden md:block" />

            {done ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="quote-success"
                className="relative h-full min-h-[420px] flex flex-col items-start justify-center"
              >
                <CheckCircle2 size={56} className="text-[#F58220] mb-6" />
                <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
                  Quote received.
                </h3>
                <p className="mt-4 text-white/80 max-w-md">
                  Our packaging consultant will reach out to you within 24 hours
                  with a tailored proposal & sample timeline.
                </p>
                <button
                  data-testid="quote-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.16em] hover:bg-[#F58220] hover:border-[#F58220] transition-colors"
                >
                  Submit another
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="relative grid grid-cols-1 md:grid-cols-2 gap-5"
                data-testid="quote-form"
              >
                <Field label="Full name *">
                  <Input
                    data-testid="quote-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Doe"
                    className={inputCls}
                    required
                  />
                </Field>

                <Field label="Company">
                  <Input
                    data-testid="quote-input-company"
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Halal & Honest Foods"
                    className={inputCls}
                  />
                </Field>

                <Field label="Email *">
                  <Input
                    data-testid="quote-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@brand.com"
                    className={inputCls}
                    required
                  />
                </Field>

                <Field label="Phone">
                  <Input
                    data-testid="quote-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91 ..."
                    className={inputCls}
                  />
                </Field>

                <Field label="Industry *">
                  <Select
                    value={form.industry}
                    onValueChange={(v) => update("industry", v)}
                  >
                    <SelectTrigger
                      data-testid="quote-select-industry"
                      className={selectCls}
                    >
                      <SelectValue placeholder="Pick an industry" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a1128] text-white border-white/20 rounded-none">
                      {INDUSTRIES.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`quote-opt-industry-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                          className="focus:bg-[#F58220] focus:text-white"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Product type *">
                  <Select
                    value={form.product_type}
                    onValueChange={(v) => update("product_type", v)}
                  >
                    <SelectTrigger
                      data-testid="quote-select-product"
                      className={selectCls}
                    >
                      <SelectValue placeholder="Pick a pouch type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a1128] text-white border-white/20 rounded-none">
                      {PRODUCTS.map((opt) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`quote-opt-product-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                          className="focus:bg-[#F58220] focus:text-white"
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Estimated MOQ" wide>
                  <Input
                    data-testid="quote-input-moq"
                    value={form.moq}
                    onChange={(e) => update("moq", e.target.value)}
                    placeholder="e.g. 5,000 pouches"
                    className={inputCls}
                  />
                </Field>

                <Field label="Tell us more" wide>
                  <Textarea
                    data-testid="quote-input-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={4}
                    placeholder="Pouch size, fill weight, barrier needs, launch date..."
                    className={`${inputCls} resize-none`}
                  />
                </Field>

                <div className="md:col-span-2 pt-2 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <p className="text-[11px] font-medium text-white/55 uppercase tracking-[0.14em]">
                    We respond within 24 working hours.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="quote-submit"
                    className="group inline-flex items-center justify-center gap-3 bg-[#F58220] hover:bg-[#d9701a] disabled:opacity-60 text-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] transition-colors"
                  >
                    {submitting ? "Sending..." : "Submit quote request"}
                    <ArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "bg-white/5 border border-white/20 text-white placeholder:text-white/40 focus-visible:border-[#F58220] focus-visible:ring-1 focus-visible:ring-[#F58220] rounded-none h-12 px-4";

const selectCls =
  "bg-white/5 border border-white/20 text-white placeholder:text-white/40 focus:border-[#F58220] focus:ring-1 focus:ring-[#F58220] rounded-none h-12 px-4";

function Field({ label, wide = false, children }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#F58220] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ Icon, label, value, href }) {
  const Inner = (
    <div className="flex items-start gap-4">
      <span className="inline-flex items-center justify-center w-10 h-10 bg-[#1B3F8F] text-white shrink-0">
        <Icon size={18} />
      </span>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#1B3F8F]">
          {label}
        </div>
        <div className="mt-1 font-semibold text-[#0a1128]">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:opacity-80 transition-opacity">
      {Inner}
    </a>
  ) : (
    Inner
  );
}
